import React, { createContext, useContext, useEffect } from 'react';

import { IBaseProps } from '@rbi-ctg/frontend';
import { useAutoEnrollUserInLoyalty } from 'hooks/use-auto-enroll-user-in-loyalty';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import useEffectOnce from 'hooks/use-effect-once';
import { usePrevious } from 'hooks/use-previous';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

import { IUseLoyaltyOffers, IUseLoyaltyRewards } from './hooks/types';
import { useGetOffersIncentivesIds } from './hooks/use-get-offers-incentives';
import { useIsLoyaltyEnabled } from './hooks/use-is-loyalty-enabled';
import { useLoyaltyOffers } from './hooks/use-loyalty-offers';
import { useLoyaltyRewards } from './hooks/use-loyalty-rewards';
import { useLoyaltyUserState } from './hooks/use-loyalty-user-state';
import { InRestaurantRedemptionProvider } from './in-restaurant-redemption';

// Leaving as an interface to be extended in future PR
export interface ILoyaltyContext extends IUseLoyaltyRewards, IUseLoyaltyOffers {
  claimPointsEnabled: boolean;
  refetchLoyaltyUser: VoidFunction;
}

export const LoyaltyContext = createContext({} as ILoyaltyContext);
export const useLoyaltyContext = () => useContext(LoyaltyContext);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const LoyaltyProvider = (props: IBaseProps) => {
  const claimPointsEnabled = Boolean(useFlag(LaunchDarklyFlag.ENABLE_CLAIM_LOYALTY_POINTS));
  const loyaltyEnabled = useIsLoyaltyEnabled();

  useAutoEnrollUserInLoyalty();

  const {
    loading: loadingLoyaltyUser,
    loyaltyUser,
    refetch: refetchLoyaltyUser,
  } = useLoyaltyUserState();
  const loyaltyUserId = loyaltyUser?.id;
  const prevLoyaltyUserId = usePrevious(loyaltyUserId);
  const loyaltyUserPoints = loyaltyUser?.points ?? 0;

  const loyaltyRewards = useLoyaltyRewards(loyaltyUser);
  const loyaltyOffers = useLoyaltyOffers();
  const { fetchIncentiveIds } = useGetOffersIncentivesIds();

  const dispatch = useAppDispatch();
  const shouldRefetchRewards = useAppSelector(selectors.loyalty.selectShouldRefetchRewards);
  const shouldRefetchOffers = useAppSelector(selectors.loyalty.selectShouldRefetchOffers);

  const { refetchRewards, engineRewardsMap } = loyaltyRewards;

  useEffectOnce(() => {
    if (loyaltyEnabled) {
      fetchIncentiveIds().then(ids => dispatch(actions.loyalty.setIncentivesIds(ids || [])));
    }
  });

  useEffectOnUpdates(() => {
    if (!loyaltyEnabled) {
      dispatch(actions.loyalty.resetLoyaltyRewardsState(loyaltyUserPoints));
    }
  }, [dispatch, loyaltyEnabled, loyaltyUserPoints]);

  // Reset rewards on loyalty user change
  useEffect(() => {
    if (prevLoyaltyUserId && !loadingLoyaltyUser && prevLoyaltyUserId !== loyaltyUserId) {
      dispatch(actions.loyalty.resetLoyaltyRewardsState(loyaltyUserPoints));
    }
    if (loyaltyUserId) {
      dispatch(actions.loyalty.setShouldRefetchRewards(true));
    }
  }, [dispatch, loadingLoyaltyUser, loyaltyUserId, loyaltyUserPoints, prevLoyaltyUserId]);

  useEffect(() => {
    if (shouldRefetchRewards && loyaltyUserId) {
      refetchRewards(loyaltyUserId);
      dispatch(actions.loyalty.setShouldRefetchRewards(false));
    }
    if (shouldRefetchOffers && loyaltyUserId) {
      loyaltyOffers.refetchUserOffers({
        loyaltyId: loyaltyUserId,
      });
      dispatch(actions.loyalty.setShouldRefetchOffers(false));
    }
  }, [dispatch, loyaltyUserId, refetchRewards, shouldRefetchRewards, shouldRefetchOffers]);

  useEffect(() => {
    const engineRewardsList = Object.values(engineRewardsMap);
    if (engineRewardsList?.length) {
      dispatch(actions.loyalty.createAvailableLoyaltyRewardsMap(engineRewardsList));
    }
  }, [dispatch, engineRewardsMap, shouldRefetchRewards]);

  return (
    <LoyaltyContext.Provider
      value={{
        claimPointsEnabled,
        refetchLoyaltyUser,
        // other loyalty hooks
        ...loyaltyOffers,
        ...loyaltyRewards,
      }}
    >
      <InRestaurantRedemptionProvider>{props.children}</InRestaurantRedemptionProvider>
    </LoyaltyContext.Provider>
  );
};
